import React, {useContext} from "react";
import {T, useTranslate} from "@tolgee/react";
import {DiscountCodeType, IDiscountCode} from "@evennt-tech/api-v2-core";
import FlexBox from "view/components/Layout/FlexBox";
import {CurrencyContext} from "view/context/CurrencyContext";
import formatPrice from "tools/formats/formatPrice";
import {Typography} from "@material-ui/core";
import {
  DISCOUNT_CODE_ERROR_MESSAGE,
  DiscountCodeError,
} from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/helpers";
import {ErrorOutline} from "@material-ui/icons";

type DiscountCode = IDiscountCode & {amount: number; error?: string};

type Props = {
  discountCodes: DiscountCode[];
};

const CartDiscountCodes = ({discountCodes}: Props) => {
  return (
    <FlexBox fullWidth column gap={1.5}>
      {discountCodes.map((discountCode) => (
        <CartDiscountCode discountCode={discountCode} key={discountCode.id} />
      ))}
    </FlexBox>
  );
};

const CartDiscountCode = ({discountCode}: {discountCode: DiscountCode}) => {
  const currency = useContext(CurrencyContext);
  const {t} = useTranslate();

  const isError = !!discountCode.error;

  const value = (() => {
    const rule = discountCode.rules?.[0];
    if (!rule) {
      return null;
    }
    const isPercentage = rule.type === DiscountCodeType.Percentage;
    const amountApplied = formatPrice(discountCode.amount, currency);

    return `(${isPercentage ? `-${rule.amount}%, ` : ""}- ${amountApplied})`;
  })();

  const description = (() => {
    if (!isError) {
      return (
        <T keyName="common.shopping_cart.discount_applied" params={{value}} />
      );
    }
    const errorMsg =
      DISCOUNT_CODE_ERROR_MESSAGE[discountCode.error as DiscountCodeError] ??
      DISCOUNT_CODE_ERROR_MESSAGE.DISCOUNT_CODE_NOT_FOUND;

    return (
      <T
        keyName="common.shopping_cart.discount_not_applied"
        params={{value, error: t(errorMsg)}}
      />
    );
  })();

  return (
    <FlexBox gap={1} fullWidth alignCenter>
      {isError && <ErrorOutline color="error" />}
      <Typography
        variant="h4"
        color={isError ? "error" : "primary"}
        style={{fontWeight: 500}}
      >
        {description}
      </Typography>
    </FlexBox>
  );
};

export default CartDiscountCodes;
